import { SITE_NAVIGATION_MENUS } from './types';
import type {
  NavigationAudience,
  SiteNavigation,
  SiteNavigationItem,
  SiteNavigationMenu
} from './types';
import { NAV_DATA } from '@lib/constants';
import type { NavBarType } from '@lib/constants';

// The top-level NAV_DATA item each Sanity menu fills. The item itself (label,
// URL, grid layout) stays in code; only its children come from Sanity.
const MENU_LEVELS: Record<SiteNavigationMenu, string> = {
  expertReveals: '3',
  content: '4',
  more: '5'
};

type AudienceContext = {
  isAuthenticated: boolean;
  isUltra: boolean;
};

const toTopLevel = (level: string): string => level.split('.')[0];

const hasLinks = (items: SiteNavigationItem[] | undefined): boolean =>
  Boolean(items?.some((item) => item.kind === 'link'));

const toNavBarItem = (item: SiteNavigationItem, level: string): NavBarType =>
  item.kind === 'heading'
    ? { name: item.label, type: 'divider', level }
    : {
        name: item.label,
        url: item.href,
        level,
        audience: item.audience,
        isOpenInNewWindow: item.shouldOpenInNewTab,
        hasGameweek: item.shouldInsertNextGameweek
      };

/**
 * NAV_DATA with each Sanity-managed menu's children replaced by the editors'
 * items, placed straight after their top-level item because the dropdowns
 * treat the first item of a level as its parent. A menu with no links in
 * Sanity keeps its NAV_DATA children, so a dropdown is never empty.
 */
export const buildNavItems = (
  siteNavigation: SiteNavigation | null | undefined,
  navData: NavBarType[] = NAV_DATA
): NavBarType[] => {
  const replacements = new Map<string, SiteNavigationItem[]>();
  SITE_NAVIGATION_MENUS.forEach((menu) => {
    const items = siteNavigation?.[menu];
    if (items && hasLinks(items)) replacements.set(MENU_LEVELS[menu], items);
  });

  return navData.flatMap((navItem) => {
    const topLevel = toTopLevel(navItem.level);
    const replacement = replacements.get(topLevel);
    if (!replacement) return [navItem];
    if (!navItem.isTopLevel) return [];

    return [
      navItem,
      ...replacement.map((item, index) =>
        toNavBarItem(item, `${topLevel}.${index + 1}`)
      )
    ];
  });
};

export const isVisibleToAudience = (
  audience: NavigationAudience | undefined,
  { isAuthenticated, isUltra }: AudienceContext
): boolean => {
  switch (audience) {
    case 'loggedOut':
      return !isAuthenticated;
    case 'members':
      return isAuthenticated;
    case 'nonUltra':
      return !isUltra;
    case 'ultra':
      return isUltra;
    default:
      return true;
  }
};

/**
 * Whichever copy editors published more recently. Pages carry the menus from
 * when they were built, so two pages in one visit can hold different copies.
 */
export const pickNewerSiteNavigation = (
  current: SiteNavigation | null,
  candidate: SiteNavigation | null
): SiteNavigation | null => {
  if (!current) return candidate;
  if (!candidate) return current;
  return candidate.updatedAt > current.updatedAt ? candidate : current;
};
